import type { ReactNode, ReactElement } from "react";
import { useRef } from "react";
import styled from "styled-components";
import { StyleTransition } from "react-transitioning";

import { Title } from "../atoms/Title";
import { useOnClickOutside } from "../../hooks/UseOnClickOutside";
import { IconTextButton } from "../atoms/IconTextButton";
import { hexToRGB } from "../../utils/style";

/**
 * [共通パーツ]ページBody
 * buttonTitleがない場合、必要なPropは「title」と「children」だけ
 * buttonTitleがある場合、更にそのボタンがメニューを出すボタンかどうかを判別する
 * メニューボタンじゃない場合「menuList」、「menuListVisible」、「onHideMenu」も必要なし
 */

type Props = {
  title: string;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonType?: "primary" | "danger";
  buttonOutline?: boolean;
  onButtonClick?: () => void;
  children: ReactNode;
  menuList?: ReactNode;
  menuListVisible?: boolean;
  onHideMenu?: () => void;
  hasBorder?: boolean;
  hasBackground?: boolean;
  searchInput?: ReactElement;
};

const styles = {
  enter: {
    opacity: 0,
  },
  enterActive: {
    opacity: 1,
    transition: "all 300ms ease-in-out",
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: "all 300ms ease-in-out",
  },
};

const Container = styled.div`
  padding: 20px;
  background-color: ${(props) => props.theme.colors.pageBodyBackground};
  width: 100%;
  min-height: 100%;
`;

const StyledPageBody = styled.div<{ $hasBorder?: boolean }>`
  border-radius: 20px;
  background-color: ${(props) => props.theme.colors.primaryBackground};
  box-shadow: 0 2px 6px ${(props) => props.theme.colors.primaryShadow};
  overflow: hidden;
  min-height: 100%;
  ${(props) =>
    props.$hasBorder &&
    `border: 1px solid ${props.theme.colors.primaryBorder}`};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 58px;
  padding: 0 40px;
  border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  position: relative;
`;

const ButtonArea = styled.div`
  margin-left: auto;
`;

const MenuList = styled.div`
  position: absolute;
  top: 48px;
  right: 20px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  background-color: ${(props) => props.theme.colors.primaryBackground};
  box-shadow: 8px 8px 8px
    ${(props) => hexToRGB(props.theme.colors.secondaryBackground, 0.1)};
`;

const SearchWrapper = styled.div`
  width: 500px;
`;

export function PageBody({
  title,
  buttonTitle,
  buttonIcon,
  onButtonClick,
  children,
  menuList,
  menuListVisible = false,
  onHideMenu,
  hasBorder = false,
  hasBackground = true,
  buttonType = "primary",
  buttonOutline = false,
  searchInput,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(ref, () => onHideMenu!());

  const Inner = (
    <StyledPageBody $hasBorder={hasBorder}>
      <Header>
        <Title text={title} />
        {searchInput && <SearchWrapper>{searchInput}</SearchWrapper>}
        {buttonTitle && (
          <ButtonArea ref={menuList ? ref : null}>
            <IconTextButton
              icon={buttonIcon}
              text={buttonTitle}
              buttonType={buttonType}
              outline={buttonOutline}
              onButtonClick={onButtonClick}
            />
            <StyleTransition
              in={menuListVisible}
              duration={300}
              styles={styles}
              enter
              exit
            >
              <MenuList>{menuList}</MenuList>
            </StyleTransition>
          </ButtonArea>
        )}
      </Header>
      {children}
    </StyledPageBody>
  );

  return hasBackground ? <Container>{Inner}</Container> : Inner;
}
